import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Button,
  Pagination,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  SmartToy,
  Person,
  ArrowBack,
  Schedule,
  Psychology,
  TrendingUp,
  WhatsApp,
  Telegram,
  Instagram
} from '@mui/icons-material';
import { analyticsApi } from '../../services/api';
/**
 * Visualizza i messaggi di una conversazione specifica con paginazione.
 * Permette di vedere i dettagli dell'utente, il tipo di messaggio (utente o AI),
 * il sentiment, il modello AI usato, i token consumati e il tempo di risposta.
 *  /\_/\
 * ( o.o )
 *  > ^ <
 */

const MessagesView = ({ conversationId, onClose, conversationTitle }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMessages, setTotalMessages] = useState(0);
  const [messagesPerPage, setMessagesPerPage] = useState(50); // Default to 50

  useEffect(() => {
    if (conversationId) {
      fetchMessages(1);
    }
  }, [conversationId, messagesPerPage]); // Refetch when page size changes

  const fetchMessages = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching messages for page:', page, 'pageSize:', messagesPerPage);
      
      const response = await analyticsApi.getConversationMessages(
        conversationId,
        page,
        messagesPerPage
      );
      
      if (response.messages) {
        setMessages(response.messages);
        setTotalMessages(response.total_count || 0);
        setTotalPages(response.total_pages || 1);
        setCurrentPage(page);
        
        // Extract user info from the first message if we haven't set it yet
        if (!userInfo && response.messages.length > 0) {
          const firstMessage = response.messages[0];
          setUserInfo({
            username: firstMessage.username,
            first_name: firstMessage.first_name,
            last_name: firstMessage.last_name,
            platform: firstMessage.platform
          });
        }
        
        console.log('Updated state:', {
          messagesCount: response.messages.length,
          totalMessages: response.total_count,
          totalPages: response.total_pages,
          currentPage: page
        });
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    fetchMessages(newPage);
  };

  const handlePageSizeChange = (event) => {
    const newPageSize = event.target.value;
    setMessagesPerPage(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const formatUsername = (username, firstName, lastName) => {
    if (username) return username;
    if (firstName && lastName) return `${firstName} ${lastName}`;
    if (firstName) return firstName;
    if (lastName) return lastName;
    return 'Anonymous User';
  };

  const getPlatformIcon = (platform) => {
    switch (platform?.toLowerCase()) {
      case 'whatsapp':
        return <WhatsApp sx={{ color: '#25D366', fontSize: 16 }} />;
      case 'telegram':
        return <Telegram sx={{ color: '#0088cc', fontSize: 16 }} />;
      case 'instagram':
        return <Instagram sx={{ color: '#E4405F', fontSize: 16 }} />;
      default:
        return null;
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getMessageIcon = (messageType) => {
    return messageType === 'ai' ? (
      <SmartToy sx={{ color: '#1976d2' }} />
    ) : (
      <Person sx={{ color: '#7b1fa2' }} />
    );
  };

  const getMessageChip = (messageType, message) => {
    if (messageType === 'ai') {
      return (
        <Chip
          icon={<SmartToy />}
          label="AI Assistant"
          size="small"
          sx={{
            bgcolor: '#1976d2',
            color: 'white',
            fontWeight: 600,
            '& .MuiChip-icon': { color: 'white' }
          }}
        />
      );
    } else {
      const username = formatUsername(message.username, message.first_name, message.last_name);
      return (
        <Chip
          icon={<Person />}
          label={username}
          size="small"
          sx={{
            bgcolor: '#2e7d32',
            color: 'white',
            fontWeight: 600,
            '& .MuiChip-icon': { color: 'white' }
          }}
        />
      );
    }
  };

  const getSentimentChip = (sentiment) => {
    if (!sentiment) return null;
    
    const sentimentConfig = {
      positive: { color: '#4caf50', bg: '#e8f5e8', label: 'Positive' },
      negative: { color: '#f44336', bg: '#ffebee', label: 'Negative' },
      neutral: { color: '#9e9e9e', bg: '#f5f5f5', label: 'Neutral' }
    };

    const config = sentimentConfig[sentiment.toLowerCase()] || sentimentConfig.neutral;
    
    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          bgcolor: config.bg,
          color: config.color,
          fontWeight: 500
        }}
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading messages...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={() => fetchMessages(1)} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center">
          <IconButton onClick={onClose} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h5" component="h2">
              Messages - {conversationTitle || `Conversation ${conversationId}`}
            </Typography>
            {userInfo && (
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                <Typography variant="subtitle2" color="text.secondary">
                  User: {formatUsername(userInfo.username, userInfo.first_name, userInfo.last_name)}
                </Typography>
                {userInfo.platform && (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {getPlatformIcon(userInfo.platform)}
                    <Typography variant="caption" color="text.secondary">
                      {userInfo.platform.charAt(0).toUpperCase() + userInfo.platform.slice(1)}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            {/* Message Statistics */}
            {!loading && totalMessages > 0 && (
              <Box display="flex" alignItems="center" gap={2} mt={1}>
                <Chip
                  label={`${totalMessages} total messages`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                {totalPages > 1 && (
                  <Chip
                    label={`Page ${currentPage} of ${totalPages}`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>
        
        {/* Pagination Controls - Top */}
        
        {totalPages > 1 && !loading && (
          <Box display="flex" alignItems="center" gap={2}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Messages per page</InputLabel>
              <Select
                value={messagesPerPage}
                label="Messages per page"
                onChange={handlePageSizeChange}
              >
                <MenuItem value={10}>10 messages</MenuItem>
                <MenuItem value={50}>50 messages</MenuItem>
                <MenuItem value={100}>100 messages</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              Showing {Math.min((currentPage - 1) * messagesPerPage + 1, totalMessages)}-{Math.min(currentPage * messagesPerPage, totalMessages)} of {totalMessages}
            </Typography>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="small"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Box>

      {/* Loading Progress */}
      {loading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
            Loading all messages... This may take a moment for conversations with many messages.
          </Typography>
        </Box>
      )}

      {/* Messages List */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {messages.length === 0 && !loading ? (
          <Box p={4} textAlign="center">
            <Typography variant="body1" color="text.secondary">
              No messages found in this conversation.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            {messages.map((message, index) => {
              const isAI = message.message_type === 'ai';
              
              return (
                <Box
                  key={message.id}
                  sx={{
                    display: 'flex',
                    justifyContent: isAI ? 'flex-start' : 'flex-end',
                    mb: 2,
                    alignItems: 'flex-start'
                  }}
                >
                  {/* AI Avatar - Left side */}
                  {isAI && (
                    <Avatar
                      sx={{
                        bgcolor: '#e3f2fd',
                        color: '#1976d2',
                        mr: 1,
                        mt: 0.5,
                        width: 40,
                        height: 40
                      }}
                    >
                      <SmartToy />
                    </Avatar>
                  )}
                  
                  {/* Message Content */}
                  <Box
                    sx={{
                      maxWidth: '70%',
                      minWidth: '200px'
                    }}
                  >
                    {/* Message Header */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isAI ? 'flex-start' : 'flex-end',
                        mb: 0.5,
                        gap: 1
                      }}
                    >
                      {getMessageChip(message.message_type, message)}
                      {getSentimentChip(message.sentiment)}
                      <Typography variant="caption" color="text.secondary">
                        {formatTimestamp(message.created_at)}
                      </Typography>
                    </Box>
                    
                    {/* Message Bubble */}
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        bgcolor: isAI ? '#f8f9ff' : '#e8f5e8',
                        borderRadius: 2,
                        borderTopLeftRadius: isAI ? 0.5 : 2,
                        borderTopRightRadius: isAI ? 2 : 0.5,
                        border: `1px solid ${isAI ? '#e3f2fd' : '#c8e6c9'}`,
                        '&:hover': {
                          bgcolor: isAI ? '#f0f2ff' : '#dcedc8'
                        }
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          color: isAI ? '#1976d2' : '#2e7d32',
                          fontWeight: isAI ? 400 : 500
                        }}
                      >
                        {message.content}
                      </Typography>
                      
                      {/* AI Metadata */}
                      {isAI && (
                        <Box display="flex" flexWrap="wrap" gap={1} mt={1.5}>
                          {message.ai_model && (
                            <Chip
                              icon={<Psychology />}
                              label={`Model: ${message.ai_model}`}
                              size="small"
                              variant="outlined"
                              sx={{
                                bgcolor: 'white',
                                color: '#1976d2',
                                borderColor: '#1976d2'
                              }}
                            />
                          )}
                          {message.ai_tokens_used && (
                            <Chip
                              icon={<TrendingUp />}
                              label={`Tokens: ${message.ai_tokens_used}`}
                              size="small"
                              variant="outlined"
                              sx={{
                                bgcolor: 'white',
                                color: '#1976d2',
                                borderColor: '#1976d2'
                              }}
                            />
                          )}
                          {message.ai_response_time && (
                            <Chip
                              icon={<Schedule />}
                              label={`${message.ai_response_time.toFixed(2)}s`}
                              size="small"
                              variant="outlined"
                              sx={{
                                bgcolor: 'white',
                                color: '#1976d2',
                                borderColor: '#1976d2'
                              }}
                            />
                          )}
                        </Box>
                      )}
                      
                      {/* Intent */}
                      {message.intent && (
                        <Box mt={1}>
                          <Chip
                            label={`Intent: ${message.intent}`}
                            size="small"
                            sx={{
                              bgcolor: '#fff3e0',
                              color: '#f57c00'
                            }}
                          />
                        </Box>
                      )}
                    </Paper>
                  </Box>
                  
                  {/* User Avatar - Right side */}
                  {!isAI && (
                    <Avatar
                      sx={{
                        bgcolor: '#f3e5f5',
                        color: '#7b1fa2',
                        ml: 1,
                        mt: 0.5,
                        width: 40,
                        height: 40
                      }}
                    >
                      <Person />
                    </Avatar>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* Bottom Pagination Controls */}
      {totalPages > 1 && totalMessages > 0 && !loading && (
        <Box display="flex" justifyContent="center" alignItems="center" mt={3} gap={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Messages per page</InputLabel>
            <Select
              value={messagesPerPage}
              label="Messages per page"
              onChange={handlePageSizeChange}
            >
              <MenuItem value={10}>10 messages</MenuItem>
              <MenuItem value={50}>50 messages</MenuItem>
              <MenuItem value={100}>100 messages</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            Page {currentPage} of {totalPages} ({totalMessages} total messages)
          </Typography>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            siblingCount={2}
            boundaryCount={1}
          />
        </Box>
      )}
    </Box>
  );
};

export default MessagesView;
